import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Flex,
  HStack,
  IconButton,
  Input,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ArrowRight, Sparkle } from "@phosphor-icons/react";
import { useChat } from "../state/useChat";
import { useSession } from "../state/useSession";
import { useWorkingPanel } from "../state/useWorkingPanel";
import { ChatMessage } from "./ChatMessage";
import { canSendMessage, composerPlaceholder } from "./canSendMessage";
import { isStageLocked } from "./stages";

// The chat is only ever a conversation. It used to open on a hero card ("Map your data with confidence")
// with suggestion chips, shown until the first message existed — which on a new session now means for about a
// second, because the assistant opens the conversation itself (SessionLayout's greeting effect).
//
// It was also a card that had to be checked against the transcript on every render, and getting that check
// wrong is invisible in the worst way: it once left the hero up throughout an upload's analysis turn, hiding
// that turn's own "Analysing …" notice and "Thinking…" indicator, so the slowest work in the product looked
// like nothing happening (ESG-3971). Anything reintroduced in this slot inherits that trap.
//
// Its chips went with it, and two of the three were already inert: "Detect column types" and "Suggest
// mappings" send messages, which the composer's own stage lock refuses on Upload & view (./stages). The
// third opened the upload modal, so **the chat now has no upload affordance of its own** — the Upload & view
// stage in the working panel owns that, with its drop zone and file-library link, and the greeting points
// the user at it.

export function ChatPanel({ onSend }) {
  const { messages, isConnected, isThinking } = useChat();
  const { hasFile, sessionKind } = useSession();
  const {
    stage,
    isOpen: isPanelOpen,
    selectionMode,
    selectedColumns,
    selectedRows,
    selectedCells,
    selectedKey,
    tableData,
    clearSelection,
  } = useWorkingPanel();

  const [input, setInput] = useState("");
  const bottomRef = useRef(null);

  // Changing data is something you do on Preview & transform, so the composer closes on Upload & view and
  // on Map & review — see ./stages. Conditional on the working panel being open, because the stage tabs
  // live in that panel: with it collapsed there would be no way to reach an unlocked stage, and the chat
  // would be shut with no way to open it.
  const lockedStage = isPanelOpen ? stage : undefined;
  const hasSourceData = hasFile || sessionKind === "foundation_v2";

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isThinking]);

  const selectionLabel = useMemo(() => {
    if (!selectionMode || !selectedKey) return null;
    if (selectionMode === "columns" && selectedColumns.length > 0) {
      const names = tableData
        ? selectedColumns.map((i) => tableData.columns[i]).join(", ")
        : "";
      return `${selectedColumns.length} col${selectedColumns.length > 1 ? "s" : ""}: ${names}`;
    }
    if (selectionMode === "rows" && selectedRows.length > 0) {
      return `${selectedRows.length} row${selectedRows.length > 1 ? "s" : ""} selected`;
    }
    if (selectionMode === "cells" && selectedCells.length > 0) {
      return `${selectedCells.length} cell${selectedCells.length > 1 ? "s" : ""} selected`;
    }
    return null;
  }, [selectionMode, selectedKey, selectedColumns, selectedRows, selectedCells, tableData]);

  // Gated here and not only on the button: Enter sends too, and the suggestion chips below call `onSend`
  // directly. A disabled button with a live Enter key is the same bug with one fewer way to notice it.
  const handleSend = () => {
    const text = input.trim();
    if (!canSendMessage({ isConnected, isThinking, text, stage: lockedStage })) return;
    onSend(text);
    setInput("");
  };

  return (
    <Flex direction="column" h="100%" w="100%" minW={0} bg="transparent">
      {/* Header */}
      <Flex px={6} pt={8} pb={5} align="center" flexShrink={0}>
        <Box>
          <HStack spacing={2}>
            {/* Matches the "AI Insights" panel heading (ReportDocEditorPanel.jsx via PageHeader/H1):
                32px icon in navIconLime (limeShade.500, #82D909), 24px (heading.l) bold title. */}
            <Sparkle size={32} color="#82D909" weight="fill" />
            <Text fontSize="24px" fontWeight="700" color="darkTeal">AI Mapping</Text>
          </HStack>
        </Box>
      </Flex>

      {/* Body */}
      <Box
        flex="1"
        overflowY="auto"
        minH={0}
        px={6}
        pb={4}
        sx={{
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": { bg: "transparent", borderRadius: "full" },
          "&:hover::-webkit-scrollbar-thumb": { bg: "gray.300" },
          scrollbarWidth: "thin",
          scrollbarColor: "transparent transparent",
          "&:hover": { scrollbarColor: "var(--chakra-colors-gray-300) transparent" },
        }}
      >
        <VStack align="stretch" spacing={3} pt={2}>
          {messages.map((msg) => (
            <ChatMessage key={msg.id} message={msg} onSend={onSend} />
          ))}
          {isThinking && (
            <HStack pl={1} pt={1} spacing={2} color="gray.500">
              <Sparkle size={14} weight="fill" color="#85C22A" />
              <Text fontSize="13px" fontStyle="italic">Thinking…</Text>
            </HStack>
          )}
          <div ref={bottomRef} />
        </VStack>
      </Box>

      {/* Selection chip */}
      {selectionLabel && (
        <HStack px={6} pb={2} flexShrink={0}>
          <Tag size="md" bg="lightLime" color="limeShade.700" border="1px solid" borderColor="lime" borderRadius="full">
            <TagLabel>{selectionLabel} in {selectedKey}</TagLabel>
            <TagCloseButton onClick={clearSelection} />
          </Tag>
        </HStack>
      )}

      {/* Composer */}
      <Box
        position="sticky"
        bottom={0}
        px={6}
        pt={3}
        pb={6}
        flexShrink={0}
        bg="gray.50"
        zIndex={2}
      >
        <Box
          border="1px solid"
          borderColor="gray.200"
          borderRadius="2xl"
          bg="white"
          boxShadow="sm"
          p={3}
          _focusWithin={{ borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" }}
        >
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder={composerPlaceholder({ isConnected, isThinking, stage: lockedStage, hasSourceData })}
            // Disabled while a turn runs, not only while disconnected: the send would be refused by the
            // session write lock the turn holds, and an input that accepts text it cannot send is worse
            // than one that says why it is closed. Same argument for a read-only stage, where the
            // placeholder names the stage that is open for changes.
            isDisabled={!isConnected || isThinking || isStageLocked(lockedStage)}
            variant="unstyled"
            fontSize="14px"
            px={1}
            mb={2}
          />
          <Flex justify="flex-end">
            <IconButton
              aria-label="Send message"
              icon={<ArrowRight size={14} weight="bold" />}
              size="sm"
              borderRadius="full"
              bg="pureBlack"
              color="white"
              _hover={{ bg: "darkTeal" }}
              onClick={handleSend}
              isDisabled={!canSendMessage({ isConnected, isThinking, text: input, stage: lockedStage })}
            />
          </Flex>
        </Box>
      </Box>
    </Flex>
  );
}
